import { getLocalStorage } from './helperFunctions';
import type { Names } from '../../types';

export function getNames(): Names | null {
  return getLocalStorage<Names>('names');
}

const NAME_FIELDS: (keyof Names)[] = [
  'team1Name',
  'team2Name',
  't1p1Name',
  't1p2Name',
  't2p1Name',
  't2p2Name',
];

export const sameNames = (a: Names, b: Names) =>
  NAME_FIELDS.every((field) => (a[field] ?? '') === (b[field] ?? ''));

const NAME_DRAFT_KEY = 'nameDraft';

/**
 * Names typed on Home but not yet saved by Start / Continue, so a refresh
 * keeps them. `base` is the saved names they were typed over: once those
 * change (a new game, different teams), the draft no longer applies.
 */
export interface NameDraft {
  base: Names;
  values: Names;
}

const isNames = (value: unknown): value is Names =>
  typeof value === 'object' &&
  value !== null &&
  NAME_FIELDS.every(
    (field) => typeof (value as Record<string, unknown>)[field] === 'string',
  );

export function getNameDraft(): NameDraft | null {
  try {
    const raw = window.localStorage.getItem(NAME_DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw) as Partial<NameDraft>;
    return isNames(draft.base) && isNames(draft.values)
      ? { base: draft.base, values: draft.values }
      : null;
  } catch {
    return null;
  }
}

export function setNameDraft(draft: NameDraft) {
  try {
    window.localStorage.setItem(NAME_DRAFT_KEY, JSON.stringify(draft));
  } catch (err) {
    console.error('Error saving the name draft:', err);
  }
}

export function clearNameDraft() {
  try {
    window.localStorage.removeItem(NAME_DRAFT_KEY);
  } catch {
    // Nothing to clear without storage.
  }
}
